import cn from "classnames";
import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";
import { TabList as PrimitiveTabList } from "react-aria-components";

import { Box } from "@/components/Box/Box";
import { SVGChevronLeft } from "@/components/SVGIcon/static/SVGChevronLeft";
import { SVGChevronRight } from "@/components/SVGIcon/static/SVGChevronRight";

import styles from "./Tabs.module.scss";
import { useCustomTabsContext } from "./Tabs.utils";

const debouncedResizeEvent = 150;

export interface TabsListProps {
  children: React.ReactNode;
}

interface ScrollArrowProps {
  direction: "left" | "right";
  onClick: () => void;
  arrowStyle?: string;
}

const ScrollArrow: React.FC<ScrollArrowProps> = ({ direction, onClick }) => {
  const isLeft = direction === "left";
  const ChevronIcon = isLeft ? SVGChevronLeft : SVGChevronRight;
  const arrowClass = isLeft ? styles.leftArrow : styles.rightArrow;

  return (
    <Box className={arrowClass} alignItems="center">
      <ChevronIcon onClick={onClick} width={16} height={16} />
    </Box>
  );
};

export const TabsList: React.FC<TabsListProps> = ({ children }) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { variant } = useCustomTabsContext();

  const updateArrows = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      // Round to the nearest integer to account for monitor rounding errors
      setCanScrollLeft(Math.floor(scrollLeft) > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < Math.floor(scrollWidth));
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const debouncedUpdateArrows = debounce(updateArrows, debouncedResizeEvent);

    scrollContainer.addEventListener("scroll", debouncedUpdateArrows);
    window.addEventListener("resize", debouncedUpdateArrows);
    const observer = new ResizeObserver(debouncedUpdateArrows);
    observer.observe(scrollContainer);

    requestAnimationFrame(updateArrows);

    return () => {
      scrollContainer.removeEventListener("scroll", debouncedUpdateArrows);
      window.removeEventListener("resize", debouncedUpdateArrows);
      observer.disconnect();
      debouncedUpdateArrows.cancel();
    };
  }, []);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === "left" ? -100 : 100;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const scrollLeft = () => scroll("left");
  const scrollRight = () => scroll("right");

  return (
    // For more versatility in spacing, consumer should add padding around the TabsList where used
    <Box className={styles.tabListContainer}>
      {canScrollLeft && <ScrollArrow direction="left" onClick={scrollLeft} />}
      <PrimitiveTabList
        className={cn(styles.tabsList, styles[`${variant}TabsList`], {
          [styles.overflowLeft]: canScrollLeft,
          [styles.overflowRight]: canScrollRight,
          [styles.overflowBoth]: canScrollLeft && canScrollRight,
        })}
        ref={scrollContainerRef}
      >
        {children}
      </PrimitiveTabList>
      {canScrollRight && <ScrollArrow direction="right" onClick={scrollRight} />}
    </Box>
  );
};
